import React from 'react';
import './ServiceCard.css';
import { Service } from '../types/Service';

interface ServiceCardProps {
  service: Service;
  isLoading: boolean;
  maintenanceMode?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, isLoading, maintenanceMode = false }) => {
  const getStatusColor = (status: string): string => {
    if (maintenanceMode && service.id === 'seedpool-main') {
      return '#f59e0b'; // Orange for maintenance
    }
    switch (status) {
      case 'operational':
        return '#10b981';
      case 'degraded':
        return '#f59e0b';
      case 'down':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string): string => {
    if (maintenanceMode && service.id === 'seedpool-main') {
      return 'Maintenance';
    }
    switch (status) {
      case 'operational':
        return 'Operational';
      case 'degraded':
        return 'Degraded';
      case 'down':
        return 'Down';
      default:
        return 'Unknown';
    }
  };

  const formatResponseTime = (ms: number): string => {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getResponseTimeColor = (ms: number): string => {
    if (ms < 500) return '#10b981';
    if (ms < 1000) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="service-card">
      <div className="service-header">
        <div className="service-info">
          <h3 className="service-name">{service.name}</h3>
          <p className="service-description">{service.description}</p>
          <span className="service-category">{service.category}</span>
        </div>
        <div className="service-status">
          <div 
            className="status-indicator"
            style={{ backgroundColor: getStatusColor(service.status) }}
          />
          <span className="status-text">{getStatusText(service.status)}</span>
        </div>
      </div>

      <div className="service-details">
        <div className="detail-row">
          <span className="detail-label">URL:</span>
          <a 
            href={service.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="service-url"
          >
            {service.id === 'irc-service' ? 'irc.seedpool.org:6697' : service.url}
          </a>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Response Time:</span>
          <span 
            className="response-time"
            style={{ color: getResponseTimeColor(service.responseTime) }}
          >
            {isLoading ? '...' : formatResponseTime(service.responseTime)}
          </span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Last Checked:</span>
          <span className="last-checked">
            {service.lastChecked.toLocaleTimeString()}
          </span>
        </div>
      </div>

      {service.status === 'down' && (
        <div className="error-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          Service is currently unavailable
        </div>
      )}

      {service.status === 'degraded' && (
        <div className="warning-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          Service experiencing issues
        </div>
      )}
    </div>
  );
};

export default ServiceCard;










